/**
 * @file: Priority bands
 *
 * The priority of an interval defines its role. It is not a measure of
 * importance. The code assigns the value, and the operator never types it.
 *
 * Each band contains spare values for peers. Two intervals in the takeover band
 * can use 200 and 201, so that overlapping peers get an order. Spare values are
 * only necessary for intervals that overlap in time. Only an overlap changes the
 * result.
 *
 * One rule assigns a value. The interval the operator acted on gets the highest
 * value in the band, for the time it shares with its peers. raiseAbovePeers
 * is that rule, and there is no second rule.
 *
 * A lowest-free-value rule was tried and dropped. With that rule the result came
 * from the previous values of the peers, not from the gesture of the operator.
 * Above a peer with 200 the new interval got 201 and took priority. Above a peer
 * with 201 it got 200 and lost priority.
 *
 * The play button on scene content is the only writer of the override band. The
 * calendar does not write it.
 *
 * The module is in shared/ because the calendar panel tells what a save puts
 * on screen. It must use the values that the server stores.
 */

const BASIS_PRIORITY = 100      // The general playlist of the day.
const TAKEOVER_PRIORITY = 200   // A named act placed over the basis.
const OVERRIDE_PRIORITY = 300   // Only the play button writes this value.

// A band contains this many values before the next band starts. Only peers that
// overlap in time use the spare values. The deepest stack in a festival week is
// two, so almost all values stay unused.
const BAND_SIZE = 100

const ROLE_BANDS = Object.freeze({
  basis: BASIS_PRIORITY,
  takeover: TAKEOVER_PRIORITY
})

const BAND_PLACEMENT = Object.freeze({ UNDER: 'under', WITHIN: 'within', OVER: 'over' })

/**
 * Give the place of a priority against the band that starts at a base value.
 *
 * The raise, the role of a stored value and the layers in the calendar panel
 * all compare a priority with a band. This function is the only comparison.
 *
 * Number() reads null and an empty string as 0, so they are under every band.
 * The player never selects such an interval, so it is under the others.
 *
 * @param {number|string} priority - A stored value can arrive as a string
 * @param {number} base - The lowest value of the band
 * @returns {string|null} A BAND_PLACEMENT value, or null for a value that is not a number
 */
function placementInBand (priority, base) {
  const value = Number(priority)

  if (!Number.isFinite(value)) {
    return null
  }

  if (value < base) {
    return BAND_PLACEMENT.UNDER
  }

  return value < base + BAND_SIZE ? BAND_PLACEMENT.WITHIN : BAND_PLACEMENT.OVER
}

const isInBand = (priority, base) => placementInBand(priority, base) === BAND_PLACEMENT.WITHIN

/**
 * Give a value in the band of the role, above every overlapping peer in that band.
 *
 * The interval the operator acted on gets the highest value among its peers. The
 * function raises that one interval. It never lowers another one, because the
 * code does not rewrite an interval the operator did not act on. The value
 * therefore climbs above the top peer instead of filling a free value below it.
 *
 * With no overlapping peer in the band, the interval gets the base value of the
 * band. Two takeovers on different days both get 200. This prevents a new value
 * for every interval created in one sitting.
 *
 * The function ignores values from the other bands. A takeover outranks its
 * takeover peers, but it does not outrank an override.
 *
 * The rule refuses a raise in the override band and a raise in a full band. A
 * caller that only predicts a save must tell these refusals apart from a
 * defect, so a refusal is a value. An unknown role is a defect, so it throws.
 *
 * @param {string} role - A key of ROLE_BANDS
 * @param {Array<number|string>} takenByOverlaps - Priorities of the intervals that
 *   this one overlaps. A stored value can arrive as a string, so the code compares
 *   them as numbers.
 * @returns {Object} { priority }, or { refusal } with the reason for the operator
 */
function raiseAbovePeers (role, takenByOverlaps) {
  const band = ROLE_BANDS[role]

  if (band === undefined) {
    // roleFromPriority reports 'override' for a row the play button wrote, so
    // the calendar can show the word. ROLE_BANDS leaves it out on purpose: the
    // play button owns that band. The two are consistent, but a bare unknown
    // role read as a defect to the operator.
    if (role === 'override') {
      return { refusal: 'The play button decides what is on screen now. This interval cannot take the stretch.' }
    }

    throw new Error(`Unknown role '${role}'. Use one of: ${Object.keys(ROLE_BANDS).join(', ')}`)
  }

  const peers = takenByOverlaps
    .filter(value => isInBand(value, band))
    .map(Number)

  if (peers.length === 0) {
    return { priority: band }
  }

  const top = Math.max(...peers)

  if (!isInBand(top + 1, band)) {
    // The operator never sees a band or a priority number. The refusal comes
    // from a new interval, a role change and Put on screen, so it names no
    // button. Each raise takes the next value, so many raises fill the band,
    // also with one peer.
    return {
      refusal: 'No free priority is left for this role at this time. ' +
        'Move or delete an overlapping interval with the same role, then save again.'
    }
  }

  return { priority: top + 1 }
}

/**
 * Give the role of a stored priority.
 *
 * The server sends the role, and the client sends a role back. The client
 * reads the numbers only to tell what a save puts on screen.
 *
 * @param {number|string} priority
 * @returns {string|null} A key of ROLE_BANDS, 'override', or null when the value
 *   predates the roles and falls in no band
 */
function roleFromPriority (priority) {
  if (isInBand(priority, OVERRIDE_PRIORITY)) return 'override'
  if (isInBand(priority, TAKEOVER_PRIORITY)) return 'takeover'
  if (isInBand(priority, BASIS_PRIORITY)) return 'basis'

  return null
}

/**
 * Give the role of a new interval that the operator did not give a role.
 *
 * An interval that overlaps nothing is alone on the scene for its stretch, so
 * it is a base. An interval over an existing one is a takeover. The operator
 * builds the schedule in that order.
 *
 * The server uses this rule when a create has no role. The calendar panel uses
 * it to select a role before the operator does. Each side decides what counts
 * as an overlap.
 *
 * @param {boolean} overlapsAnother - True when the new interval overlaps another interval
 * @returns {string} A key of ROLE_BANDS
 */
function roleForNewInterval (overlapsAnother) {
  return overlapsAnother ? 'takeover' : 'basis'
}

/**
 * Give the priority that a save of a stored interval writes.
 *
 * The code assigns a new value only when the operator asked for an outcome.
 * The interval the operator acted on then wins the stretch it shares with its
 * peers.
 *
 * - An edit without a role keeps the stored value. A drag or a resize sends
 *   times only. A move must not change which interval appears on screen.
 * - An edit with the same role keeps the stored value too. The panel always
 *   sends a role, so an ordinary save must not change the order. For example,
 *   the operator corrects the end time of the interval that loses a shared
 *   stretch. Without this rule, that save would make the interval the winner.
 * - A role change, or the take-the-stretch button, gets a new assigned value.
 *   The operator asked for a different outcome, so the control must give one.
 *
 * The server and the calendar panel both use this function. The server reads
 * the overlaps from the database. The panel reads them from the week on show.
 *
 * @param {number|string} storedPriority
 * @param {Object} request - { role, winsPeers } from the save
 * @param {Array<number|string>} takenByOverlaps - As for raiseAbovePeers
 * @returns {Object} { priority }, or { refusal } as raiseAbovePeers gives it
 */
function priorityOnSave (storedPriority, { role, winsPeers }, takenByOverlaps) {
  if (keepsStoredPriority(storedPriority, { role, winsPeers })) {
    return { priority: storedPriority }
  }

  return raiseAbovePeers(role, takenByOverlaps)
}

function keepsStoredPriority (storedPriority, { role, winsPeers }) {
  if (!role) {
    return true
  }

  return role === roleFromPriority(storedPriority) && !winsPeers
}

export {
  BASIS_PRIORITY,
  TAKEOVER_PRIORITY,
  OVERRIDE_PRIORITY,
  BAND_SIZE,
  ROLE_BANDS,
  BAND_PLACEMENT,
  placementInBand,
  raiseAbovePeers,
  roleFromPriority,
  roleForNewInterval,
  priorityOnSave
}
